import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PatientSlotContainer from './PatientSlotContainer';
import { useStatusDisplay } from '@/hooks/useStatusColor';
import { useScope } from '@/hooks/useScope';
import isMiddleEast from '@/util/isMiddleEast';
import PatientInfo from './PatientInfo';
import BedInfo from './BedInfo';
import { INTERNAL_MONITOR, PACU_TABLET, POST_OP_TABLET, PREP_TABLET } from '../../entities/screens/enums';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles } from '@material-ui/core';
import ChatButton from '../../chat/ChatButton';
import { CaretakerChatDialog } from '../../chat/ChatDialog';
import CaretakerMessage from './CaretakerMessage';

const useStyles = makeStyles(theme =>
  createStyles({
    chatButton: {
      paddingLeft: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
  })
);

const PatientSlot = ({
  caretakerMessage,
  status,
  physician,
  procedure,
  patient,
  patientId,
  patientType,
  bed,
  showPriority,
  priority,
  timeEntered,
  poseRef,
  preventAnimation,
  isFamilyReadyPreOp,
  ready,
  readyForSurgeon,
  readyForOr,
  isHoldProcedure,
  isBlockNerve,
  frameColor,
  familyReady,
  readyForPickup,
  size,
  roomName,
  screenType,
  expectedExitTime,
  hideBedInfo,
  patientData,
  room,
  preOpFocused,
  showPreOpNote,
  preOpNote,
  showPacuNote,
  pacuNote,
  showPatientNote,
  patientNote,
  hideChart,
  isDPU,
  showRoom,
  showDuration,
  hospitalOverviewMode,
  blockNerveFollow,
  called,
  transferredToChair,
  transferredToPT,
  ptComplete,
  isPreOp,
  isPacu,
  messagingView,
  readyForAnesthesia,
  isSpinalTimeout,
  showLatePreOpIndicator,
  isSuperAdmin,
  isGroupAdmin,
  showPatientAgeAndSex,
  showPatientProcedureTimes,
  showPatientScheduledOr,
  firstCase,
}) => {
  const classes = useStyles();
  const isInternalScreen = screenType === INTERNAL_MONITOR;
  const { getContourStyle, getFlashingContourColor } = useStatusDisplay('panel', { skip: !isInternalScreen });
  const scope = useScope();
  const isDpuAsPreOp = isMiddleEast(scope?.hospital?.id);
  const statusRoom = room?.type ? room : patientData?.room;
  const flashingContourColor = isInternalScreen
    ? getFlashingContourColor(patientData?.status, statusRoom, isDpuAsPreOp)
    : undefined;
  const isPreOpScreen = screenType === PREP_TABLET;
  const isPacuScreen = screenType === PACU_TABLET;
  const isPostOpScreen = screenType === POST_OP_TABLET;
  const useCompactProcedureTimeLayout = Boolean(showPatientProcedureTimes || showPatientScheduledOr);
  const showChatIcon =
    !messagingView &&
    !!patientData?.notificationNumbers &&
    !!patientData?.caretakerConsent &&
    (isPacuScreen || isPreOpScreen || isPostOpScreen);

  const isDelayed = useIsDelayed(isPreOp, patientData?.procedure?.startTime || patientData?.procedureData?.startTime);
  const isPacuLate = useIsPacuLate(patientData);

  return (
    <Box>
      <PatientSlotContainer
        status={status}
        ref={poseRef}
        preventAnimation={preventAnimation}
        style={isInternalScreen ? getContourStyle(patientData?.status, statusRoom, isDpuAsPreOp) : undefined}
        border={(ready || isFamilyReadyPreOp) && !isDPU ? '#00B63E' : flashingContourColor || frameColor}
        size={size}
        messagingView={messagingView}
        $compactProcedureTimeLayout={useCompactProcedureTimeLayout}
        $showScheduledOr={Boolean(showPatientScheduledOr)}
      >
        <PatientInfo
          physician={physician}
          patient={patient}
          patientId={patientId}
          patientType={patientType}
          procedure={procedure}
          timeEntered={timeEntered}
          ready={ready}
          readyForSurgeon={readyForSurgeon}
          readyForOr={readyForOr}
          familyReady={familyReady}
          readyForPickup={readyForPickup}
          isHoldProcedure={isHoldProcedure}
          isBlockNerve={isBlockNerve}
          bed={bed}
          size={size}
          roomName={roomName}
          expectedExitTime={expectedExitTime}
          showPreOpNote={showPreOpNote}
          showPacuNote={showPacuNote}
          showPatientNote={showPatientNote}
          preOpNote={preOpNote}
          pacuNote={pacuNote}
          patientNote={patientNote}
          hideChart={hideChart}
          isDPU={isDPU}
          showRoom={showRoom}
          showDuration={showDuration}
          blockNerveFollow={blockNerveFollow}
          called={called}
          transferredToChair={transferredToChair}
          transferredToPT={transferredToPT}
          ptComplete={ptComplete}
          isPreOp={isPreOp}
          isPacu={isPacu}
          noAdditionalInfo={messagingView}
          patientData={patientData}
          screenType={screenType}
          hospitalOverviewMode={hospitalOverviewMode}
          messagingView={messagingView}
          readyForAnesthesia={readyForAnesthesia}
          isSpinalTimeout={isSpinalTimeout}
          showLatePreOpIndicator={isDelayed && showLatePreOpIndicator}
          showPacuLateIndicator={isPacuLate}
          isSuperAdmin={isSuperAdmin}
          isGroupAdmin={isGroupAdmin}
          showPatientAgeAndSex={showPatientAgeAndSex}
          showPatientProcedureTimes={showPatientProcedureTimes}
          showPatientScheduledOr={showPatientScheduledOr}
          firstCase={firstCase}
        />
        <Box className={classes.chatButton} style={useCompactProcedureTimeLayout ? { paddingLeft: 0 } : undefined}>
          {showChatIcon && (
            <ChatButton
              hasUnreadMessages={patientData?.caretakerThread?.hasUnreadMessages}
              chatComponent={CaretakerChatDialog}
              chatProps={{
                patient: patientData,
              }}
            />
          )}
        </Box>
        {isInternalScreen && caretakerMessage && !messagingView && (
          <CaretakerMessage phoneNumber={caretakerMessage?.phoneNumber} message={caretakerMessage?.content} />
        )}
        {!hideBedInfo && (
          <BedInfo preOpFocused={preOpFocused} bed={bed} showPriority={showPriority} priority={priority} size={size} />
        )}
      </PatientSlotContainer>
    </Box>
  );
};

PatientSlot.propTypes = {
  patient: PropTypes.string.isRequired,
};

export default PatientSlot;

export const useIsDelayed = (isPreOp, startTime) => {
  const [isDelayed, setDelayed] = useState(() => isPreOp && startTime && new Date() > new Date(startTime));

  useEffect(() => {
    if (!isPreOp || !startTime) {
      setDelayed(false);
      return;
    }

    const currentTime = new Date();

    if (currentTime > new Date(startTime)) {
      setDelayed(true);
      return;
    }

    const timeout = setTimeout(() => setDelayed(true), new Date(startTime) - currentTime);

    return () => {
      clearTimeout(timeout);
    };
  }, [isPreOp, startTime]);

  return isDelayed;
};

export const useIsPacuLate = patientData => {
  const [isPacuLate, setPacuLate] = useState(false);

  useEffect(() => {
    if (!patientData?.room?.pacuOutWarningThreshold || patientData?.room?.type !== 'PACU') {
      setPacuLate(false);
      return;
    }

    const pacuEntryTime = patientData?.timeEntered;
    if (!pacuEntryTime) {
      setPacuLate(false);
      return;
    }

    const currentTime = new Date();
    const threshold = patientData.room.pacuOutWarningThreshold;
    const pacuEntryDate = new Date(pacuEntryTime);
    const thresholdTime = pacuEntryDate.getTime() + threshold * 60 * 1000;

    if (currentTime.getTime() > thresholdTime) {
      setPacuLate(true);
      return;
    }

    setPacuLate(false);
    const timeout = setTimeout(() => setPacuLate(true), thresholdTime - currentTime.getTime());

    return () => {
      clearTimeout(timeout);
    };
  }, [patientData?.room?.pacuOutWarningThreshold, patientData?.room?.type, patientData?.timeEntered]);

  return isPacuLate;
};
